import { CustomSelector, ECustomSelector, Input } from '@globalmed/atlas-ui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './ProfileTab.module.css';
import { TCreateOrganizationForm } from '../../schema';
import { LocationFields } from '@/components/LocationFields';
import { translatedLanguageOptions } from '@/utils/languageOptions';

/**
 * Profile tab of the create organization modal.
 * @returns JSX.Element representing the Profile tab.
 */
export const ProfileTab = () => {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<TCreateOrganizationForm>();
  const { t } = useTranslation();
  const languageOptions = translatedLanguageOptions(t);

  return (
    <div className={styles.flexColumn}>
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            label={t('admin-panel.organizationNameLabel')}
            placeholder={t(
              'create-organization-modal.organization-name-placeholder',
            )}
            width="100%"
            disabled={isSubmitting}
            {...field}
            isError={!!fieldState.error}
            message={t(fieldState.error?.message ?? '')}
            required
          />
        )}
      />

      <Controller
        name="language"
        control={control}
        render={({ field, fieldState }) => (
          <CustomSelector
            type={ECustomSelector.Select}
            labelProps={{
              label: t('create-organization-modal.organization-language'),
            }}
            options={languageOptions}
            selected={field.value}
            error={t(fieldState.error?.message ?? '')}
            onChange={field.onChange}
            sx={{ maxWidth: '600px', width: '100%' }}
          />
        )}
      />

      <div className={styles.locationWrap}>
        <LocationFields stateLabel="common.state-territory" />
      </div>

      <Controller
        name="address1"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            label={t('create-organization-modal.address-line-1')}
            placeholder={t(
              'create-organization-modal.address-line-1-placeholder',
            )}
            width="100%"
            disabled={isSubmitting}
            {...field}
            value={field.value ?? ''}
            isError={!!fieldState.error?.message}
            message={t(fieldState.error?.message ?? '')}
          />
        )}
      />

      <Controller
        name="address2"
        control={control}
        render={({ field, fieldState }) => (
          <Input
            label={t('create-organization-modal.address-line-2')}
            placeholder={t(
              'create-organization-modal.address-line-2-placeholder',
            )}
            width="100%"
            disabled={isSubmitting}
            {...field}
            value={field.value ?? ''}
            isError={!!fieldState.error?.message}
            message={t(fieldState.error?.message ?? '')}
          />
        )}
      />

      <div className={styles.block}>
        <Controller
          name="zip"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              label={t('common.zipCodeLabel')}
              placeholder={t('common.zipCodePlaceholder')}
              width="100%"
              disabled={isSubmitting}
              {...field}
              value={field.value ?? ''}
              isError={!!fieldState.error?.message}
              message={t(fieldState.error?.message ?? '')}
            />
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              label={t('common.city')}
              placeholder={t('common.city')}
              width="100%"
              disabled={isSubmitting}
              {...field}
              value={field.value ?? ''}
              isError={!!fieldState.error?.message}
              message={t(fieldState.error?.message ?? '')}
            />
          )}
        />
      </div>
    </div>
  );
};
